import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, X } from "lucide-react";
import axios from "axios";
import { useEnquiry } from "../context/EnquiryContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-ink/25 px-4 py-3 text-sm placeholder:text-ink/35 focus:outline-none focus:border-ink transition-colors";

export const EnquiryModal = () => {
  const { open, product, closeEnquiry } = useEnquiry();
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    requirement: "",
    quantity: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (open) {
      setStatus("idle");
      setForm((f) => ({ ...f, requirement: product || f.requirement }));
    }
  }, [open, product]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        requirement: form.requirement.trim(),
      };
      if (form.company.trim()) payload.company = form.company.trim();
      if (form.email.trim()) payload.email = form.email.trim();
      if (product) payload.product = product;
      if (form.quantity.trim()) payload.quantity = form.quantity.trim();
      if (form.message.trim()) payload.message = form.message.trim();
      await axios.post(`${API}/enquiries`, payload);
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="enquiry-modal"
          className="fixed inset-0 z-[80] flex justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <button
            data-testid="enquiry-overlay-close"
            aria-label="Close enquiry form"
            onClick={closeEnquiry}
            className="absolute inset-0 bg-ink/60 cursor-default"
          />
          <motion.div
            className="relative h-full w-full max-w-xl bg-paper border-l border-ink overflow-y-auto"
            data-lenis-prevent
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between px-6 sm:px-10 py-6 border-b border-ink/15">
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink/50">
                Enquiry — Jagdamba Enterprises
              </p>
              <button
                data-testid="enquiry-close-button"
                onClick={closeEnquiry}
                aria-label="Close"
                className="p-2 -mr-2 hover:text-ink/60 transition-colors"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            {status === "success" ? (
              <div className="px-6 sm:px-10 py-16" data-testid="enquiry-success-message">
                <span className="inline-flex h-14 w-14 items-center justify-center bg-accent text-ink mb-8">
                  <Check size={26} strokeWidth={2} />
                </span>
                <h3 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl leading-none mb-6">
                  Thank you.
                </h3>
                <p className="text-ink/70 leading-relaxed max-w-md">
                  Your requirement has been received. The Jagdamba Enterprises team will get in
                  touch with you.
                </p>
                <button
                  data-testid="enquiry-done-button"
                  onClick={closeEnquiry}
                  className="mt-10 bg-ink text-paper font-mono text-[11px] tracking-[0.2em] uppercase px-7 py-4 hover:bg-accent hover:text-ink transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-6 sm:px-10 py-10">
                <h3 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl leading-[0.95] mb-2">
                  {product ? (
                    <>
                      Enquire About
                      <br />
                      <span className="underline decoration-accent decoration-4 underline-offset-8">
                        {product}
                      </span>
                    </>
                  ) : (
                    "Send Your Requirement"
                  )}
                </h3>
                <p className="text-sm text-ink/60 mt-4 mb-10">
                  Tell us what you need. We&apos;ll take it from there.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="enquiry-name" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">
                      Name *
                    </label>
                    <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                  </div>
                  <div>
                    <label htmlFor="enquiry-company" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">
                      Company
                    </label>
                    <input id="enquiry-company" data-testid="enquiry-company-input" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company name" />
                  </div>
                  <div>
                    <label htmlFor="enquiry-phone" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">
                      Phone *
                    </label>
                    <input id="enquiry-phone" data-testid="enquiry-phone-input" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91" />
                  </div>
                  <div>
                    <label htmlFor="enquiry-email" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">
                      Email
                    </label>
                    <input id="enquiry-email" data-testid="enquiry-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="enquiry-requirement" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">
                      Product / Requirement *
                    </label>
                    <input id="enquiry-requirement" data-testid="enquiry-product-input" required value={form.requirement} onChange={set("requirement")} className={inputCls} placeholder="e.g. Hex Bolt, Hydraulic Oil" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="enquiry-quantity" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">
                      Quantity / Approximate Requirement
                    </label>
                    <input id="enquiry-quantity" data-testid="enquiry-quantity-input" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder="e.g. 500 pcs, 1 drum" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="enquiry-message" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">
                      Message
                    </label>
                    <textarea id="enquiry-message" data-testid="enquiry-message-input" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Any specifications, sizes, material grade (MS / GI / SS)…" />
                  </div>
                </div>

                {status === "error" && (
                  <p data-testid="enquiry-error-message" className="mt-5 text-sm text-red-700">
                    Something went wrong. Please try again, or call us directly.
                  </p>
                )}

                <button
                  data-testid="enquiry-submit-button"
                  type="submit"
                  disabled={status === "sending"}
                  className="mt-8 w-full bg-ink text-paper font-mono text-xs tracking-[0.2em] uppercase px-6 py-4 flex items-center justify-center gap-2 hover:bg-accent hover:text-ink transition-colors disabled:opacity-60"
                >
                  {status === "sending" ? "Sending…" : "Send Requirement"}
                  <ArrowUpRight size={14} />
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
